import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    CcReviewComplianceHistory,
    CcReviewComplianceHistoryCreate,
    CcReviewComplianceItemState,
} from "server/models"
import {
    IDLE, PENDING,
    WORKFLOW_LEVEL_ASSIGNED,
    WORKFLOW_RERUN,
} from "const"
import { StateType, CcStateSettingsType } from "client/utils"
import { ContentColumn } from "client/parts/layout"
import {
    StoreType,

    LoadCcReviewComplianceRevisionData,
    UpdateCcReviewComplianceVerifier,
    CreateManyCcReviewComplianceHistory,

    initReviewComplianceHistory,
    setCcReviewComplianceFormalSelectedItemIds,
    setCcReviewComplianceSelectedItemIds,
    setCcReviewComplianceItemLoadState,
} from "client/store"
import {
    Empty,
    SelectDropdown,
    SelectDropdownItem,
    UserIcon,
    Button,
    SpinnerIcon,

    TableGroupedIds,
    TableNestedIds,
    MoveTableBottom,
} from "client/components"

import InputSelectAll from "./selectAll"

interface BottomProps {
    formal: boolean
}

const Bottom = ({
    formal,
}: BottomProps) => {
    const version = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const formalTableClass = useSelector((state: StoreType) => state.ccReviewCompliance.formalTableClass)
    const reviewTableClass = useSelector((state: StoreType) => state.ccReviewCompliance.reviewTableClass)
    const tableClass = formal ? formalTableClass : reviewTableClass

    const setList = useSelector((state: StoreType) => state.ccReviewCompliance.setList)
    const itemList = useSelector((state: StoreType) => state.ccReviewCompliance.itemList)
    const itemGroupedIds = useSelector((state: StoreType) => state.ccReviewCompliance.itemGroupedIds)
    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewCompliance.revisionActiveList)
    const itemStateList = useSelector((state: StoreType) => state.ccReviewCompliance.itemStateList)
    const itemHistoryList = useSelector((state: StoreType) => state.ccReviewCompliance.itemHistoryList)

    const formalSelectedItemIds = useSelector((state: StoreType) => state.ccReviewCompliance.formalSelectedItemIds)
    const formalVisibleItemIds = useSelector((state: StoreType) => state.ccReviewCompliance.formalVisibleItemIds)
    const reviewSelectedItemIds = useSelector((state: StoreType) => state.ccReviewCompliance.reviewSelectedItemIds)
    const reviewVisibleItemIds = useSelector((state: StoreType) => state.ccReviewCompliance.reviewVisibleItemIds)
    const selectedItemIds = formal ? formalSelectedItemIds : reviewSelectedItemIds
    const visibleItemIds = formal ? formalVisibleItemIds : reviewVisibleItemIds

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const reviewLevelIds = moduleLevelList[2] || []
    const formalLevelIds = moduleLevelList[0] || []
    const levelIds = formal ? formalLevelIds : reviewLevelIds

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [visibleSelected, setVisibleSelected] = useState<number[]>([])
    const [selectedLevelId, setSelectedLevelId] = useState<number>(null)
    const [selectedUserId, setSelectedUserId] = useState<number>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)

    const table = document.getElementsByClassName(tableClass)[0] as HTMLElement
    const scrollableParent = document.body.querySelector(ContentColumn)

    useEffect(() => {
        const selected: number[] = []

        const addNestedItems = (nested: readonly TableNestedIds[]) => {
            nested.forEach((item) => {
                const itemId = Number(item.id)
                // sub-items of top level can be selected but hidden
                if (selectedItemIds.includes(itemId)) selected.push(itemId)
                addNestedItems(item.nestedIds)
            })
        }
        const checkNestedGroups = (nested: readonly TableGroupedIds[]) => {
            nested.forEach((group) => {
                group.nestedIds.forEach((nestedItem) => { // top level of nested items
                    const itemId = Number(nestedItem.id)
                    if (visibleItemIds.includes(itemId)) {
                        if (selectedItemIds.includes(itemId)) selected.push(itemId)
                        addNestedItems(nestedItem.nestedIds)
                    }
                })
                checkNestedGroups(group.nestedGroupedIds)
            })
        }
        checkNestedGroups(Object.values(itemGroupedIds))

        setVisibleSelected(selected)
    }, [visibleItemIds, selectedItemIds])

    useEffect(() => {
        fixBottomOnScroll(null, true)

        scrollableParent.addEventListener("scroll", fixBottomOnScroll)
        return () => scrollableParent.removeEventListener("scroll", fixBottomOnScroll)
    }, [visibleSelected])

    const fixBottomOnScroll = (e?: MouseEvent, forceDisplay?: boolean) => {
        if (!table) return
        if (visibleSelected.length > 0) {
            const windowHeight = window.innerHeight
            const tableBounding = table.getBoundingClientRect()
            const tableHeight = tableBounding.height
            const offsetTop = tableBounding.top
            const diff = windowHeight - (tableHeight + offsetTop)

            if (diff > 50) { // when table is scrolled to down move its bottom to its place
                MoveTableBottom(table, false)
            } else if (diff < -50) { // when table starts to scroll from bottom to up
                MoveTableBottom(table, true)
            } else if (forceDisplay) {
                MoveTableBottom(table, true)
            }
        } else {
            MoveTableBottom(table, false)
        }
    }

    const changeLevel = (levelId: number) => {
        setSelectedLevelId(levelId)
        setSelectedUserId(null)
    }

    const saveAssigned = () => {
        const stateArray: CcReviewComplianceItemState[] = []
        const historyArray: CcReviewComplianceHistoryCreate[] = []
        const promiseArray: Promise<any>[] = []

        visibleSelected.forEach((itemId) => {
            const item = itemList[itemId]
            const itemState = (itemStateList[item.librarySetId] && itemStateList[item.librarySetId][item.id]) || null
            const itemHistory = (itemHistoryList[item.librarySetId] && itemHistoryList[item.librarySetId][item.id]) || []
            const revision = revisionActiveList[version.id] && revisionActiveList[version.id][item.librarySetId]

            let lastHistory: CcReviewComplianceHistory = null

            // get the last history row made by user, not by 'system'
            for (let i = itemHistory.length - 1; i >= 0; i--) {
                const history = itemHistory[i]
                if (history.workflow === WORKFLOW_RERUN) {
                    break
                }
                if (history.systemMessage === false) {
                    lastHistory = history
                    break
                }
            }

            const settings: CcStateSettingsType = itemState && itemState.settings
                ? JSON.parse(itemState.settings)
                : {}
            const prevUserId = settings[selectedLevelId]

            if (prevUserId !== selectedUserId) {
                settings[selectedLevelId] = selectedUserId

                const firstLevelId = levelIds.length ? levelIds[0] : null
                const nextLevelId = (itemState && itemState.levelId) || firstLevelId
                const nextVerifierId = (itemState && itemState.verifierId) || null

                stateArray.push({
                    itemId,
                    reviewComplianceRevisionId: revision.id,
                    levelId: nextLevelId,
                    verifierId: nextLevelId === selectedLevelId ? selectedUserId : nextVerifierId,
                    settings: JSON.stringify(settings),
                })
                historyArray.push({
                    ...initReviewComplianceHistory,
                    itemId,
                    reviewComplianceRevisionId: revision.id,
                    parentId: lastHistory && lastHistory.nextLevelId ? lastHistory.id : null,
                    prevUserId: prevUserId || null,
                    prevLevelId: selectedLevelId,
                    nextUserId: selectedUserId,
                    workflow: WORKFLOW_LEVEL_ASSIGNED,
                    systemMessage: true,
                    editorId: userData.userId,
                })
            }
        })

        promiseArray.push(
            UpdateCcReviewComplianceVerifier(stateArray),
            CreateManyCcReviewComplianceHistory(historyArray),
        )

        setSaveState(PENDING)
        Promise.all(promiseArray).then(() => {
            dispatch(setCcReviewComplianceFormalSelectedItemIds([]))
            dispatch(setCcReviewComplianceSelectedItemIds([]))
            setSelectedLevelId(null)
            setSelectedUserId(null)
            setSaveState(IDLE)

            // reload revision data
            const promiseReloadArray: Promise<any>[] = []
            Object.values(setList).forEach((set) => {
                const revision = revisionActiveList[version.id][set.id]
                promiseReloadArray.push(
                    LoadCcReviewComplianceRevisionData(revision, dispatch),
                )
            })

            dispatch(setCcReviewComplianceItemLoadState(PENDING))
            Promise.all(promiseReloadArray).then(() => {
                dispatch(setCcReviewComplianceItemLoadState(IDLE))
            })
        })
    }

    const selectedLevelName = selectedLevelId
        ? levelList[selectedLevelId].name
        : lang.selectLevel

    const groupId = selectedLevelId
        ? levelList[selectedLevelId].groupId
        : null

    const userIds = groupId
        ? groupUserList[groupId] || []
        : []

    return (
        <>
            <InputSelectAll title={`${lang.assign}:`} formal={formal} />

            <SelectDropdown
                title={selectedLevelName}
                row="true"
            >
                {
                    levelIds && levelIds.map((levelId) => {
                        const level = levelList[levelId]
                        return (
                            <SelectDropdownItem
                                key={levelId}
                                onClick={() => changeLevel(levelId)}
                            >
                                {level.name}
                            </SelectDropdownItem>
                        )
                    })
                }
            </SelectDropdown>

            <SelectDropdown
                row="true"
                title={selectedUserId
                    ? <UserIcon user={userList[selectedUserId]} size="sm" />
                    : lang.selectUser}
            >
                {
                    userIds && userIds.map((userId) => {
                        const user = userList[userId]
                        return (
                            <SelectDropdownItem
                                key={userId}
                                onClick={() => setSelectedUserId(userId)}
                            >
                                <UserIcon user={user} size="sm" />
                            </SelectDropdownItem>
                        )
                    })
                }
                {
                    userIds && userIds.length === 0
                    && <SelectDropdownItem><Empty /></SelectDropdownItem>
                }
            </SelectDropdown>

            <Button
                variant="success"
                disabled={visibleSelected.length === 0 || selectedLevelId === null || selectedUserId === null}
                onClick={() => saveAssigned()}
            >
                {saveState === PENDING ? <SpinnerIcon /> : lang.assign}
            </Button>
        </>
    )
}
export default Bottom
